import * as React from 'react';

import { css, StyleSheet } from 'aphrodite';
import { Col, Container, Row } from 'reactstrap';
import Select from 'react-select';
import newtheme from '../../newtheme';
import SingleUseCase, { ISingleUseCase } from './SingleUseCase';
import { mpTrackButtonClick } from '../../tracking/mixpanel';
import { MpTrackingButtons } from '../../config/buttons';
import { ILandingPageUseCases } from '../../domaindriven/landing/types/ILandingPageUseCases';

interface IProps {
    sectionData: ILandingPageUseCases;
}

interface IState {
    selectedUseCase: ISingleUseCase;
}

class UseCases extends React.Component<IProps, IState> {
    constructor(props) {
        super(props);

        this.state = { selectedUseCase: this.getOptions()[0] };
    }

    public handleChange = (selectedOption: ISingleUseCase) => {
        this.setState({ selectedUseCase: selectedOption });
        mpTrackButtonClick(selectedOption.value.trackingButton);
    };

    public getOptions = () => {
        const options: ISingleUseCase[] = [
            {
                label: 'EXERCISING',
                value: {
                    trackingButton: MpTrackingButtons.SELECT_USE_CASE_EXERCISING,
                    image: {
                        url: '/usecase_exercising.png',
                        backgroundPosition: '85% 0%',
                    },
                    time: {
                        duration: '18',
                        unit: 'minutes',
                        activity: 'Session',
                    },
                    outcome: {
                        value: '1',
                        text: 'Series bringing you from A-Z on topics that matter.',
                    },
                },
            },
            {
                label: 'SHOWERING',
                value: {
                    trackingButton: MpTrackingButtons.SELECT_USE_CASE_SHOWERING,
                    image: {
                        url: '/usecase_showering.png',
                        backgroundPosition: 'top',
                    },
                    time: {
                        duration: '7',
                        unit: 'minutes',
                        activity: 'Showering',
                    },
                    outcome: {
                        value: '1',
                        text: 'Insightful article.',
                    },
                },
            },
            {
                label: 'COOKING',
                value: {
                    trackingButton: MpTrackingButtons.SELECT_USE_CASE_COOKING,
                    image: {
                        url: '/usecase_cooking.png',
                        backgroundPosition: 'top',
                    },
                    time: {
                        duration: '27',
                        unit: 'minutes',
                        activity: 'In the kitchen',
                    },
                    outcome: {
                        value: '2',
                        text: 'thought provoking long-form articles.',
                    },
                },
            },
            {
                label: 'WALKING',
                value: {
                    trackingButton: MpTrackingButtons.SELECT_USE_CASE_WALKING,
                    image: {
                        url: '/usecase_walking.png',
                        backgroundPosition: 'top',
                    },
                    time: {
                        duration: '45',
                        unit: 'minutes',
                        activity: 'Outdoors',
                    },
                    outcome: {
                        value: '7',
                        text: 'articles offering fresh perspectives.',
                    },
                },
            },
            {
                label: 'FALLING ASLEEP',
                value: {
                    trackingButton: MpTrackingButtons.SELECT_USE_CASE_SLEEPING,
                    image: {
                        url: '/usecase_sleeping.png',
                        backgroundPosition: 'top',
                    },
                    time: {
                        duration: '12',
                        unit: 'minutes',
                        activity: 'Dozing Off',
                    },
                    outcome: {
                        value: '2k',
                        text: 'words of a quality story.',
                    },
                },
            },
        ];

        return options;
    };

    public render() {
        const { sectionData } = this.props;

        return (
            <div className={css(styles.main)}>
                <Container>
                    <Row>
                        <Col xl={{ size: 8, offset: 2 }}>
                            <h3 className={css(styles.heading)}>{sectionData.title}</h3>
                        </Col>
                    </Row>
                    <Row>
                        <Col className={css(styles.useCaseSelector)}>
                            <p className={css(styles.useCaseText)}>I want to learn while</p>
                            <Select
                                options={this.getOptions()}
                                className={css(styles.useCaseDropdown)}
                                styles={customSelectStyes}
                                // @ts-ignore
                                onChange={this.handleChange}
                                value={this.state.selectedUseCase}
                                isSearchable={false}
                            />
                        </Col>
                    </Row>
                    <SingleUseCase useCase={this.state.selectedUseCase} />
                </Container>
            </div>
        );
    }
}

const customSelectStyes = {
    container: (provided) => ({
        ...provided,
        color: 'white',
        border: 'none',
        cursor: 'pointer',
    }),
    option: (provided) => ({
        ...provided,
        ':hover': {
            opacity: 0.7,
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 14,
        },
        borderTop: '1px solid #e5f6ff',
        backgroundColor: '#CAEDFF',
        padding: '15px 20px',
        color: newtheme.colors.primaryBlue,
        fontWeight: 600,
        fontSize: 18,
        cursor: 'pointer',
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: '#CAEDFF',
        textAlign: 'center',
    }),
    control: (provided) => ({
        ...provided,
        border: 'none',
        backgroundColor: '#CAEDFF',
        color: 'white',
        borderRadius: 10,
        cursor: 'pointer',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: newtheme.colors.primaryBlue,
        fontWeight: 600,
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        stroke: newtheme.colors.primaryBlue,
    }),
    indicatorSeparator: (provided) => ({
        ...provided,
        display: 'none',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        color: newtheme.colors.primaryBlue,
        hover: {
            color: newtheme.colors.primaryBlue,
        },
    }),
    valueContainer: (provided) => ({
        ...provided,
        [newtheme.cssBreakpoints.large]: {
            padding: '10px 15px',
        },
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            padding: '5px 15px',
        },
        [newtheme.cssBreakpoints.medium]: {
            padding: '5px 15px',
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 18,
        },
        padding: '15px 15px',
    }),
    singleValue: (provided) => ({
        ...provided,
        [newtheme.cssBreakpoints.large]: {
            fontSize: 22,
        },
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            fontSize: 18,
        },
        [newtheme.cssBreakpoints.medium]: {
            fontSize: 18,
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 16,
        },
        fontSize: 24,
        fontWeight: 600,
        color: newtheme.colors.primaryBlue,
    }),
};

const styles = StyleSheet.create({
    main: {
        [newtheme.cssBreakpoints.medium]: {
            paddingTop: 40,
            paddingBottom: 120,
        },
        [newtheme.cssBreakpoints.tiny]: {
            paddingBottom: 100,
        },
        paddingTop: 80,
        paddingBottom: 160,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: 'white',
        color: 'black',
    },
    heading: {
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            fontSize: 40,
        },
        [newtheme.cssBreakpoints.large]: {
            fontSize: 40,
        },
        [newtheme.cssBreakpoints.medium]: {
            fontSize: 30,
        },
        [newtheme.cssBreakpoints.tiny]: {
            fontSize: 24,
            marginBottom: 15,
        },
        fontSize: 48,
        fontWeight: 500,
        textAlign: 'center',
        marginBottom: 28,
    },
    useCaseSelector: {
        [newtheme.cssBreakpoints.medium]: {
            marginBottom: 40,
        },
        [newtheme.cssBreakpoints.small]: {
            marginBottom: 30,
        },
        [newtheme.cssBreakpoints.tiny]: {
            flexDirection: 'column',
        },
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 65,
    },
    useCaseText: {
        [newtheme.cssBreakpoints.large]: {
            fontSize: 22,
        },
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            fontSize: 20,
        },
        [newtheme.cssBreakpoints.medium]: {
            fontSize: 20,
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 16,
        },
        [newtheme.cssBreakpoints.tiny]: {
            marginBottom: 5,
        },
        fontSize: 24,
        fontWeight: 400,
        margin: 0,
        marginRight: 20,
    },
    useCaseDropdown: {
        [newtheme.cssBreakpoints.largeWidthLargeHeight]: {
            width: 250,
        },
        [newtheme.cssBreakpoints.medium]: {
            width: 250,
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 18,
        },
        width: 300,
    },
});

export default UseCases;
